<template>
  <section class="card">
    <header class="head">
      <h2>确认函模板管理</h2>
      <button type="button" class="btn btn-primary" @click="openCreateDialog">新增模板</button>
    </header>
    <p class="note">
      维护不同级别可用的确认函模板；「秘书确认」即审核通过并发确认函，请在「入会申请记录」中选择模板后操作（支持单条或批量）。
    </p>

    <div class="filters filters-panel">
      <div class="flt">
        <input
          v-model.trim="keyword"
          class="flt-input flt-input--wide"
          placeholder="搜索模板名称/级别"
          aria-label="搜索模板名称或级别"
          @keyup.enter="applyFilters"
        />
      </div>
      <div class="flt">
        <FancySelect v-model="statusFilter" class="flt-select" :options="toolbarStatusOptions" placeholder="模板状态" />
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-primary filter-action-btn" @click="applyFilters">查询</button>
        <button type="button" class="btn btn-cancel filter-action-btn" @click="resetFilters">重置</button>
      </div>
    </div>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <table v-if="rows.length">
      <thead>
        <tr>
          <th class="col-seq">序号</th>
          <th>模板名称</th>
          <th>适用级别</th>
          <th class="col-status">状态</th>
          <th>文件</th>
          <th class="col-center col-ops">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(r, idx) in displayRows" :key="r.id">
          <td class="col-seq">{{ idx + 1 }}</td>
          <td>{{ r.templateName }}</td>
          <td>{{ r.applyLevel || '通用' }}</td>
          <td class="col-status">
            <span class="pill" :class="r.statusCode === 'ENABLED' ? 'pill-green' : 'pill-red'">
              {{ r.statusCode === 'ENABLED' ? '启用' : '停用' }}
            </span>
          </td>
          <td class="url-cell">
            <span class="file-link" :title="r.fileUrl">{{ fileName(r.fileUrl) }}</span>
          </td>
          <td class="col-center col-ops actions">
            <button type="button" class="btn btn-edit" @click="previewRow(r)">预览</button>
            <button type="button" class="btn btn-edit" @click="editRow(r)">编辑</button>
            <button type="button" class="btn btn-delete" @click="removeRow(r)">删除</button>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else class="empty-state">
      <p class="empty-title">暂无模板</p>
      <p class="empty-desc">先上传一个确认函模板，入会申请记录里就可以批量发放了。</p>
      <button type="button" class="btn btn-primary" @click="openCreateDialog">立即新增模板</button>
    </div>

    <div v-if="showDialog" class="mask" @click.self="closeDialog">
      <div class="dialog dialog--shell">
        <div class="dialog-head">
          <h3 class="dialog-title">{{ form.id ? '编辑模板' : '新增模板' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeDialog">×</button>
        </div>
        <div class="dialog-body">
<div class="form">
          <label class="field">
            <span>模板名称</span>
            <input v-model.trim="form.templateName" placeholder="请输入模板名称" />
          </label>
          <label class="field">
            <span>适用级别（可空）</span>
            <input v-model.trim="form.applyLevel" placeholder="例如：副会长单位" />
          </label>
          <label class="field">
            <span>状态</span>
            <FancySelect v-model="form.statusCode" :options="formStatusOptions" placeholder="选择状态" />
          </label>
          <label class="field field-full">
            <span>模板文件</span>
            <div class="file-line">
              <input :value="form.fileUrl" placeholder="请先上传模板文件" readonly />
              <button type="button" class="btn btn-ghost" @click="pickFile">{{ form.fileUrl ? '更换文件' : '上传文件' }}</button>
            </div>
            <p v-if="form.fileUrl" class="file-hint" :title="form.fileUrl">{{ form.fileUrl }}</p>
            <p class="file-hint muted">请上传 .docx，需填处写为双花括号占位，如：单位名称、入会日期（发送时系统会自动识别并弹框填写）。</p>
          </label>
        </div>
        </div>

        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="closeDialog">取消</button>
          <button type="button" class="btn btn-primary" :disabled="saving" @click="saveTemplate">
            {{ saving ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="previewVisible" class="mask" @click.self="closePreview">
      <div class="dialog dialog--shell dialog-preview">
        <div class="dialog-head">
          <h3 class="dialog-title">模板预览</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closePreview">×</button>
        </div>
        <div class="dialog-body">
<div class="preview-wrap">
          <iframe v-if="previewUrl" :src="previewUrl" class="preview-frame" />
          <p v-else class="muted">该文件类型暂不支持嵌入预览，可点击下方“新窗口打开”。</p>
        </div>
        </div>

        <div class="dialog-foot">
          <a class="btn btn-edit" :href="toFullUrl(previewRawUrl)" target="_blank" rel="noopener">新窗口打开</a>
          <button type="button" class="btn btn-primary" @click="closePreview">关闭</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm } from '@/utils/adminDialog'
import FancySelect from '@/components/FancySelect.vue'
import {
  BASE_URL,
  uploadFile,
  getMembershipConfirmTemplates,
  createMembershipConfirmTemplate,
  updateMembershipConfirmTemplate,
  deleteMembershipConfirmTemplate
} from '@/api'

const emptyForm = () => ({
  id: 0,
  templateName: '',
  applyLevel: '',
  fileUrl: '',
  statusCode: 'ENABLED',
  remark: ''
})

export default {
  name: 'ConfirmTemplateManageView',
  components: { FancySelect },
  data() {
    return {
      sourceRows: [],
      rows: [],
      keyword: '',
      statusFilter: '',
      toolbarStatusOptions: [
        { value: '', label: '全部状态' },
        { value: 'ENABLED', label: '启用' },
        { value: 'DISABLED', label: '停用' },
      ],
      formStatusOptions: [
        { value: 'ENABLED', label: '启用' },
        { value: 'DISABLED', label: '停用' },
      ],
      form: emptyForm(),
      saving: false,
      errorMsg: '',
      showDialog: false,
      previewVisible: false,
      previewUrl: '',
      previewRawUrl: ''
    }
  },
  mounted() {
    this.reload()
  },
  computed: {
    /** 列表按数据库 id 升序，序号列与展示顺序一致 */
    displayRows() {
      return (this.rows || []).slice().sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0))
    },
  },
  methods: {
    toFullUrl(url) {
      const raw = String(url || '').trim()
      if (!raw) return '#'
      if (/^https?:\/\//i.test(raw)) return raw
      return `${BASE_URL}${raw.startsWith('/') ? '' : '/'}${raw}`
    },
    async reload() {
      try {
        this.errorMsg = ''
        this.sourceRows = (await getMembershipConfirmTemplates()) || []
        this.applyFilters()
      } catch (e) {
        this.errorMsg = e.message || '读取模板失败'
      }
    },
    applyFilters() {
      const kw = String(this.keyword || '').trim().toLowerCase()
      const status = String(this.statusFilter || '').trim().toUpperCase()
      this.rows = (this.sourceRows || []).filter((x) => {
        const hitStatus = !status || String(x.statusCode || '').toUpperCase() === status
        if (!hitStatus) return false
        if (!kw) return true
        const text = `${x.templateName || ''} ${x.applyLevel || ''}`.toLowerCase()
        return text.includes(kw)
      })
    },
    resetFilters() {
      this.keyword = ''
      this.statusFilter = ''
      this.applyFilters()
    },
    openCreateDialog() {
      this.resetForm()
      this.showDialog = true
    },
    closeDialog() {
      this.showDialog = false
    },
    resetForm() {
      this.form = emptyForm()
    },
    editRow(r) {
      this.form = {
        id: Number(r.id || 0),
        templateName: String(r.templateName || ''),
        applyLevel: String(r.applyLevel || ''),
        fileUrl: String(r.fileUrl || ''),
        statusCode: String(r.statusCode || 'ENABLED'),
        remark: String(r.remark || '')
      }
      this.showDialog = true
    },
    fileName(url) {
      const u = String(url || '').split('?')[0]
      const seg = u.split('/').pop() || ''
      try {
        const decoded = decodeURIComponent(seg)
        // 存储层会带 UUID 前缀（如 uuid_originalName.docx），列表展示时去掉前缀更友好
        const pretty = decoded.replace(
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i,
          '',
        )
        return pretty || decoded || '查看文件'
      } catch {
        const pretty = seg.replace(
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i,
          '',
        )
        return pretty || seg || '查看文件'
      }
    },
    pickFile() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.pdf,.doc,.docx'
      input.onchange = async () => {
        const file = input.files && input.files[0]
        if (!file) return
        try {
          this.errorMsg = ''
          const res = await uploadFile(file)
          this.form.fileUrl = String((res && res.url) || '')
        } catch (e) {
          this.errorMsg = e.message || '文件上传失败'
        }
      }
      input.click()
    },
    async saveTemplate() {
      const payload = {
        templateName: String(this.form.templateName || '').trim(),
        applyLevel: String(this.form.applyLevel || '').trim(),
        fileUrl: String(this.form.fileUrl || '').trim(),
        statusCode: String(this.form.statusCode || 'ENABLED').toUpperCase(),
        remark: String(this.form.remark || '').trim()
      }
      if (!payload.templateName) {
        this.errorMsg = '模板名称不能为空'
        return
      }
      if (!payload.fileUrl) {
        this.errorMsg = '请先上传模板文件'
        return
      }
      try {
        this.saving = true
        this.errorMsg = ''
        if (Number(this.form.id || 0) > 0) {
          await updateMembershipConfirmTemplate(this.form.id, payload)
        } else {
          await createMembershipConfirmTemplate(payload)
        }
        this.resetForm()
        this.closeDialog()
        await this.reload()
      } catch (e) {
        this.errorMsg = e.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
    async removeRow(row) {
      if (!await adminConfirm(`确认删除模板「${row.templateName || row.id}」吗？`)) return
      try {
        this.errorMsg = ''
        await deleteMembershipConfirmTemplate(row.id)
        await this.reload()
      } catch (e) {
        this.errorMsg = e.message || '删除失败'
      }
    },
    previewRow(row) {
      const raw = String(row && row.fileUrl ? row.fileUrl : '').trim()
      if (!raw) return
      this.previewRawUrl = raw
      this.previewUrl = this.buildPreviewUrl(raw)
      this.previewVisible = true
    },
    closePreview() {
      this.previewVisible = false
      this.previewUrl = ''
      this.previewRawUrl = ''
    },
    buildPreviewUrl(url) {
      const full = this.toFullUrl(url)
      const lower = full.toLowerCase().split('?')[0]
      if (lower.endsWith('.pdf')) return full
      // Office 在线预览服务对公网可达性要求高，易出现 "An error occurred"；
      // 这里不再强制嵌入，统一走“新窗口打开”更稳定。
      return ''
    }
  }
}
</script>

<style scoped>
/* 与轮播管理（views/cms/BannerManageView）一致的卡片、表头、表格与按钮 */
.card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
}
.head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.head h2 {
  margin: 0;
  font-size: 18px;
  color: #101828;
}
.note {
  margin: 0 0 12px;
  background: #f5f8ff;
  border: 1px solid #dae5ff;
  color: #2a4b8d;
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
}
.filters .flt-input {
  width: 180px;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  background: #fff;
}
.filters .flt-input--wide {
  width: 280px;
}
.filters .flt-select {
  width: 170px;
}
:deep(.filters .fs-btn) {
  height: 40px;
  min-width: 170px;
  border-radius: 8px;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th,
td {
  border-bottom: 1px solid #edf0f5;
  padding: 8px;
  font-size: 13px;
  text-align: left;
}
.col-seq,
.col-status {
  text-align: center;
  vertical-align: middle;
}
.ellipsis {
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.btn {
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  border: 1px solid transparent;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  line-height: 1;
}
.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.btn + .btn {
  margin-left: 8px;
}
.btn-primary {
  background: #2f6af6;
  color: #fff;
}
.btn-edit {
  border-color: #bfc9db;
  background: #fff;
  color: #26457d;
}
.btn-cancel {
  border-color: #d0d7e2;
  background: #fff;
  color: #344054;
}
.btn-ghost {
  color: #2f6af6;
  background: #eef4ff;
  border-color: #d7e4ff;
}
.btn-delete {
  border-color: #ffc8c2;
  background: #fff1f0;
  color: #d92d20;
}
.error {
  color: #d92d20;
  margin: 0 0 8px;
}
.pill {
  display: inline-block;
  min-width: 58px;
  text-align: center;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
}
.pill-green {
  color: #067647;
  background: #ecfdf3;
  border: 1px solid #abefc6;
}
.pill-red {
  color: #b42318;
  background: #fef3f2;
  border: 1px solid #fecdca;
}
.file-hint {
  margin: 0 0 12px;
  color: #475467;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.file-link {
  display: inline-block;
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #2f6af6;
}
.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.actions .btn + .btn {
  margin-left: 0;
}
.dialog .empty-state {
  border: 1px dashed #cfd8ee;
  border-radius: 12px;
  background: #fbfcff;
  padding: 36px 20px;
  text-align: center;
}
.empty-title {
  margin: 0;
  font-size: 18px;
  color: #1d2939;
}
.empty-desc {
  margin: 8px 0 16px;
  color: #667085;
}
.dialog {
  width: 760px;
  max-width: calc(100vw - 40px);
  background: #fff;
  border-radius: 12px;
  padding: 16px;
}
.dialog h3 {
  margin: 0 0 12px;
}
.form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: #344054;
}
.field > span {
  font-weight: 600;
}
.field-full {
  grid-column: 1 / -1;
}
.file-line {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: center;
}
.form input {
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
  box-sizing: border-box;
}
:deep(.form .fs-btn) {
  height: 40px;
  border-radius: 8px;
}
.dialog-preview {
  width: 980px;
  max-width: calc(100vw - 40px);
}
.preview-wrap {
  border: 1px solid #e4eaf6;
  border-radius: 10px;
  height: 68vh;
  background: #fff;
  overflow: hidden;
}
.preview-frame {
  width: 100%;
  height: 100%;
  border: 0;
}
.muted {
  padding: 16px;
  color: #667085;
}
</style>
